// public/js/services/analysis/UtilityMethods.js
export const UtilityMethods = {
    calculateGrowth(data) {
        if (data.length < 2) return 0;
        const first = data[0].wishlists || data[0].count || 0;
        const last = data[data.length - 1].wishlists || data[data.length - 1].count || 0;
        return first > 0 ? ((last - first) / first) : 0;
    },

    calculateVelocity(data) {
        if (data.length < 2) return 0;
        const changes = [];
        for (let i = 1; i < data.length; i++) {
            const prev = data[i - 1].wishlists || data[i - 1].count || 0;
            const curr = data[i].wishlists || data[i].count || 0;
            changes.push(curr - prev);
        }
        return this.calculateAverage(changes);
    },

    calculateAcceleration(data) {
        if (data.length < 3) return 0;
        const velocities = [];
        for (let i = 1; i < data.length; i++) {
            velocities.push(data[i].wishlists - data[i - 1].wishlists);
        }
        const velocityChanges = [];
        for (let i = 1; i < velocities.length; i++) {
            velocityChanges.push(velocities[i] - velocities[i - 1]);
        }
        return this.calculateAverage(velocityChanges);
    },

    calculatePercentageChange(oldValue, newValue) {
        return oldValue > 0 ? ((newValue - oldValue) / oldValue) : 0;
    },

    calculateMomentum(history) {
        const recentData = history.slice(-7);
        let positiveChanges = 0;
        for (let i = 1; i < recentData.length; i++) {
            if (recentData[i].revenue > recentData[i - 1].revenue) {
                positiveChanges++;
            }
        }
        return positiveChanges / (recentData.length - 1);
    },

    determinePriceTier(price) {
        if (price === 0) return 'Free';
        if (price < 5) return 'Budget';
        if (price < 20) return 'Mid';
        if (price < 40) return 'Premium';
        return 'AAA';
    },

    isPriceCompetitive(price, competitorPrices) {
        if (competitorPrices.length === 0) return true;
        const avg = this.calculateAverage(competitorPrices);
        return price <= avg * 1.1; // Within 10% of average
    },
    findUniqueFeatures(game, details) {
        const uniqueFeatures = [];
        const competitorFeatures = new Set();

        details.audienceOverlap?.forEach(competitor => {
            competitor.features?.forEach(feature => {
                competitorFeatures.add(feature);
            });
        });

        game.features?.forEach(feature => {
            if (!competitorFeatures.has(feature)) {
                uniqueFeatures.push(feature);
            }
        });

        return uniqueFeatures;
    },

    hasPriceAdvantage(price, details) {
        if (!details.audienceOverlap?.length) return false;
        const competitorPrices = details.audienceOverlap
            .map(c => c.price)
            .filter(Boolean);
        return this.isPriceCompetitive(price, competitorPrices);
    },

    determineTargetAudience(game) {
        const tags = game.tags || [];
        if (tags.includes('Casual')) return 'casual';
        if (tags.includes('Hardcore')) return 'hardcore';
        return 'core';
    },

    estimateMarketSize(game) {
        const totalOwnersInGenre = (game.audienceOverlap || [])
            .reduce((sum, competitor) => sum + (competitor.owners || 0), 0);
        return {
            total: totalOwnersInGenre,
            potential: Math.round(totalOwnersInGenre * 0.1) // Assume 10% market capture potential
        };
    },

    needsMoreContent(game) {
        const avgPlaytime = game.avgPlaytime || 0;
        const price = game.price || 0;
        return (price > 15 && avgPlaytime < 10) || (price > 30 && avgPlaytime < 20);
    },

    hasPricingOpportunity(game) {
        const reviewScore = game.reviewScore || 0;
        const price = game.price || 0;
        return (reviewScore > 85 && price < 15) || (reviewScore < 70 && price > 20);
    },

    hasMarketExpansionPotential(details) {
        if (!details || !details.countryData) {
            console.log("No country data found for game, skipping market expansion analysis.");
            return false;
        }

        const regions = Object.keys(details.countryData || {});
        return regions.length < 3; // Could expand to more regions
    },

    identifyFeatureGaps(details) {
        if (!details || !details.features) {
            console.log("No feature data found for game, skipping feature gap analysis.");
            return [];
        }

        const commonFeatures = new Set(['Achievements', 'Cloud Saves', 'Controller Support']);
        const currentFeatures = new Set(details.features || []);
        return [...commonFeatures].filter(feature => !currentFeatures.has(feature));
    },

    calculateOverallTrends(games) {
        const recentGames = games.filter(g => this.isRecent(g));
        return {
            revenueGrowth: this.calculateAverageGrowth(recentGames, 'revenue'),
            wishlistGrowth: this.calculateAverageGrowth(recentGames, 'wishlists'),
            playerGrowth: this.calculateAverageGrowth(recentGames, 'players')
        };
    },

    isRecent(game) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return new Date(game.releaseDate) >= thirtyDaysAgo;
    },

    calculateRecommendationPriority(recommendation, game) {
        // Priority scale: 1-10
        switch (recommendation) {
            case 'Increase visibility':
                return game.wishlists < 1000 ? 10 : 5;
            case 'Target new regions':
                return Object.keys(game.countryData || {}).length < 3 ? 8 : 4;
            case 'Optimize store tags':
                return game.tags?.length < 5 ? 7 : 3;
            default:
                return 5;
        }
    },

    estimateRecommendationImpact(recommendation, game) {
        // Impact percentage estimate
        switch (recommendation) {
            case 'Increase visibility':
                return '15-25%';
            case 'Target new regions':
                return '20-30%';
            case 'Optimize store tags':
                return '10-15%';
            default:
                return '5-10%';
        }
    },

    estimateDevImpact(recommendations, game) {
        let totalImpact = 0;

        recommendations.forEach(rec => {
            if (rec.includes('Add more content')) {
                totalImpact += 0.3; // 30% potential impact
            }
            if (rec.includes('missing features')) {
                totalImpact += 0.15; // 15% potential impact
            }
            if (rec.includes('player retention')) {
                totalImpact += 0.25; // 25% potential impact
            }
        });

        return {
            potentialRevenue: this.estimatePotentialRevenue(totalImpact, game),
            timeToRealize: this.estimateTimeToRealize(recommendations),
            priority: totalImpact > 0.5 ? 'High' : totalImpact > 0.3 ? 'Medium' : 'Low'
        };
    },

    estimatePotentialRevenue(impactFactor, game) {
        const currentRevenue = game.revenue || 0;
        return currentRevenue * (1 + impactFactor);
    },

    estimateTimeToRealize(recommendations) {
        let weeks = 0;
        recommendations.forEach(rec => {
            if (rec.includes('Add more content')) weeks += 8;
            if (rec.includes('missing features')) weeks += 4;
            if (rec.includes('player retention')) weeks += 6;
        });
        return `${weeks} weeks`;
    },

    // Core utility functions
    sum(numbers) {
        return numbers.reduce((a, b) => a + (b || 0), 0);
    },

    calculateAverage(numbers) {
        if (!numbers.length) return 0;
        return this.sum(numbers) / numbers.length;
    },

    createBatches(items, size) {
        const batches = [];
        for (let i = 0; i < items.length; i += size) {
            batches.push(items.slice(i, i + size));
        }
        return batches;
    },

    findDifferentiators(game, details) {
        const differentiators = [];

        // Unique selling points
        const uniqueFeatures = this.findUniqueFeatures(game, details);
        if (uniqueFeatures.length > 0) {
            differentiators.push('Unique Features');
        }

        // Price advantage
        if (this.hasPriceAdvantage(game.price, details)) {
            differentiators.push('Competitive Pricing');
        }

        // Quality indicators
        if (details.reviewScore > 85) {
            differentiators.push('High Quality');
        }

        return differentiators;
    },

    calculateStrengthScore(advantages) {
        // Convert advantages into a numerical score
        return advantages.reduce((score, advantage) => {
            switch (advantage) {
                case 'High Rating':
                    return score + 3;
                case 'Strong Following':
                    return score + 2;
                case 'Competitive Pricing':
                    return score + 2;
                default:
                    return score + 1;
            }
        }, 0);
    }
};